/* eslint-disable @typescript-eslint/no-explicit-any */
// src/services/api/branding-service.ts
import request from "umi-request";
import { environment } from "@/environment/environment";

/* ---------------- Types ---------------- */

export type ApiResponse<T = any> = {
  data?: T;
  success?: boolean;
  message?: string;
};

export type ServerBrandingDto = {
  id?: string;
  clientId: string;
  clientCode?: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    loaderColor: string;
    backgroundColor: string;
  };
  assets?: {
    logo?: string;
    favicon?: string;
    icon192?: string;
    splash512?: string;
  };
  content: {
    appName: string;
    shortName: string;
    loginTitle: string;
    loginSubtitle: string;
    dashboardTitle: string;
    termsAndConditions?: string;
  };
  features: {
    enableWhatsApp: boolean;
    enablePanicButton: boolean;
    enableForms: boolean;
    enableBrochure: boolean;
    enableSMS: boolean;
    enableGoogleMaps: boolean;
  };
  version?: string;
};

export type Branding = {
  id?: string;
  clientId: string;
  primaryColor: string;
  secondaryColor: string;
  loaderColor: string;
  backgroundColor: string;
  appName: string;
  shortName: string;
  loginTitle: string;
  loginSubtitle: string;
  dashboardTitle: string;
  termsAndConditions?: string;
  enableWhatsApp: boolean;
  enablePanicButton: boolean;
  enableForms: boolean;
  enableBrochure: boolean;
  enableSMS: boolean;
  enableGoogleMaps: boolean;
  assets?: { logo?: string; favicon?: string; icon192?: string; splash512?: string };
  version?: string;
};

/* ---------------- Helpers ---------------- */

// Single, shared API base for everything (branding calls + assets + manifest)
export const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ||
  environment.baseUrl ||
  ""
).replace(/\/+$/, "");

const unwrap = <T,>(r: ApiResponse<T> | T): T =>
  (r && typeof r === "object" && "data" in (r as any) ? (r as any).data : r) as T;

// join base + path without `//`
const joinUrl = (base?: string, path?: string) => {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  const b = (base || "").replace(/\/+$/, "");
  const p = path.replace(/^\/+/, "");
  return `${b}/${p}`;
};

const withApiBase = (p?: string | null) => (p ? joinUrl(API_BASE, p) : undefined);

/**
 * Get the tenant key from the current URL.
 * - Strips the Vite base (e.g. "/pwa_local")
 * - Returns the first segment after the base, unless it’s a known route name
 */
export function deriveBrandKeyFromLocation(): string | null {
  const BASE = (import.meta.env.BASE_URL || "/").replace(/\/+$/, ""); // no trailing slash
  const raw = window.location.pathname;
  const pathAfterBase = raw.startsWith(BASE) ? raw.slice(BASE.length) : raw;

  const firstSeg = pathAfterBase.replace(/^\/+/, "").split("/")[0] || "";
  if (!firstSeg) return null;

  // Keep this list in sync with src/App.route.tsx
  const RESERVED = new Set(
    [
      // public
      "login",
      "register",
      "registeruploads",
      "forgotpassword",
      "reset-password",

      // protected (tenant-internal)
      "dashboard",
      "profile",
      "mycover",
      "additionalservices",
      "accidentsupport",
      "accidentsupportlist",
      "uploads",
      "vehicle",
      "vehicleaddedit",
      "vehicleuploads",
      "vehiclelist",
      "dynamicform",
      "dynamiclist",
      "dynamicfromview",
      "singleupload",
      "accidentsupportdraw",
      "accidentsupportfinal",
      "viewaccidentsupport",
      "addresslist",
      "addeditaddress",
      "address",
    ].map((s) => s.toLowerCase())
  );

  if (RESERVED.has(firstSeg.toLowerCase())) return null;

  // tenant keys like "hollard", "client-1", "acme123"
  return /^[a-z0-9-]+$/i.test(firstSeg) ? firstSeg : null;
}

/** Map server DTO -> app Branding model */
export const serverToBranding = (s: ServerBrandingDto): Branding => ({
  id: s.id,
  clientId: s.clientId,
  primaryColor: s.theme?.primaryColor ?? "#1976d2",
  secondaryColor: s.theme?.secondaryColor ?? "#0d47a1",
  loaderColor: s.theme?.loaderColor ?? s.theme?.primaryColor ?? "#1976d2",
  backgroundColor: s.theme?.backgroundColor ?? "#ffffff",
  appName: s.content?.appName ?? "Digicall-Alert",
  shortName: s.content?.shortName ?? "Digicall-Alert",
  loginTitle: s.content?.loginTitle ?? "Welcome",
  loginSubtitle: s.content?.loginSubtitle ?? "Sign in to your account",
  dashboardTitle: s.content?.dashboardTitle ?? "Dashboard",
  termsAndConditions: s.content?.termsAndConditions,
  enableWhatsApp: !!s.features?.enableWhatsApp,
  enablePanicButton: !!s.features?.enablePanicButton,
  enableForms: !!s.features?.enableForms,
  enableBrochure: !!s.features?.enableBrochure,
  enableSMS: !!s.features?.enableSMS,
  enableGoogleMaps: !!s.features?.enableGoogleMaps,
  assets: {
    logo: withApiBase(s.assets?.logo),
    favicon: withApiBase(s.assets?.favicon),
    icon192: withApiBase(s.assets?.icon192),
    splash512: withApiBase(s.assets?.splash512),
  },
  version: s.version,
});

/* ---------------- API calls ---------------- */

export async function GetBrandingByKey(
  key: string,
  signal?: AbortSignal
): Promise<ApiResponse<ServerBrandingDto> | null> {
  try {
    const resp = await request<ApiResponse<ServerBrandingDto>>(
      `/branding/by-key/${encodeURIComponent(key)}`,
      {
        method: "GET",
        prefix: API_BASE,
        headers: { Accept: "application/json" },
        requestType: "json",
        signal,
      }
    );
    return resp ?? null;
  } catch (e: any) {
    // console.error("GetBrandingByKey error", {
    //   name: e?.name,
    //   message: e?.message,
    //   status: e?.response?.status,
    //   hasResponse: !!e?.response,
    // });

    // Abort => navigation/unmount/etc – don’t show any error UI
    if (e?.name === "AbortError") {
      return null;
    }

    // If the server actually responded with 404, surface that as a
    // normal ApiResponse so the caller can treat it as "unknown tenant".
    if (e?.response?.status === 404) {
      return {
        data: undefined,
        success: false,
        message: "404",
      } as ApiResponse<ServerBrandingDto>;
    }

    // Everything else (TLS / cert / DNS / CORS / 500 / etc.) is treated as a
    // "certificate / connection" style problem.
    const err = new Error("BrandingNetworkError");
    err.name = "BrandingNetworkError";
    (err as any).cause = e;
    throw err;
  }
}

export async function LoadBrandingFlat(key: string, signal?: AbortSignal) {
  if (!key) return null;

  const resp = await GetBrandingByKey(key, signal);

  // null means “aborted” – caller keeps the loader
  if (!resp) return null;

  // Explicit “unknown tenant” – the API responded with a 404
  if (resp.success === false && resp.message === "404") {
    const err = new Error("UnknownTenant");
    err.name = "UnknownTenant";
    throw err;
  }

  const dto = unwrap<ServerBrandingDto>(resp);
  return dto ? serverToBranding(dto) : null;
}

export async function LoadBrandingForCurrentTenant(signal?: AbortSignal) {
  const key = deriveBrandKeyFromLocation();
  if (!key) return null;
  return LoadBrandingFlat(key, signal);
}
